import React from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Smartphone, Apple, Image as ImageIcon, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SectionHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { SUPPORT_EMAIL } from "@/components/landing/LandingUI";
import { PACKAGE_ID, LISTING, ANDROID_STEPS, IOS_STEPS } from "@/content/storeKit";

const SHOTS = ["1-dashboard", "2-latihan", "3-detail-gerakan", "4-program-pemula", "5-progres", "6-pencapaian"];

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Disalin"); } catch { toast.error("Gagal menyalin"); }
};

function Card({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border bg-card p-5 sm:p-6" data-testid={testid}>
      <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold"><Icon className="h-5 w-5 text-primary" /> {title}</h2>
      {children}
    </section>
  );
}

function InfoRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b py-2.5 last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium" data-testid={testid}>{value}</div>
      </div>
      <Button size="icon" variant="ghost" onClick={() => copy(value)} aria-label={`Salin ${label}`}><Copy className="h-4 w-4" /></Button>
    </div>
  );
}

function Asset({ src, label, className }) {
  return (
    <a href={src} download className="group block" data-testid={`asset-${label}`}>
      <img src={src} alt={label} loading="lazy" className={`w-full rounded-xl border bg-muted object-cover transition-transform duration-200 group-hover:scale-[1.02] ${className}`} />
      <span className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground"><Download className="h-3 w-3" /> {label}</span>
    </a>
  );
}

function Steps({ items, testid }) {
  return (
    <ol className="space-y-3" data-testid={testid}>
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">{i + 1}</span>{s}
        </li>
      ))}
    </ol>
  );
}

export default function StoreKit() {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  const origin = window.location.origin;

  return (
    <div className="space-y-6" data-testid="store-kit-page">
      <SectionHeader title="Kit Publish Aplikasi" subtitle="Semua yang dibutuhkan untuk publish FitBersama ke Play Store & App Store" />
      <Card icon={FileText} title="Info Aplikasi" testid="store-info">
        <InfoRow label="Package ID / Bundle ID" value={PACKAGE_ID} testid="store-package-id" />
        <InfoRow label="Alamat aplikasi (untuk PWABuilder)" value={origin} testid="store-app-url" />
        <InfoRow label="URL Kebijakan Privasi" value={`${origin}/privacy`} />
        <InfoRow label="URL Syarat Layanan" value={`${origin}/terms`} />
        <InfoRow label="Hapus akun (Profil → Hapus Akun)" value={`${origin}/profile`} />
        <InfoRow label="Email kontak" value={SUPPORT_EMAIL} />
      </Card>
      <Card icon={FileText} title="Teks Halaman Toko" testid="store-listing">
        <InfoRow label="Judul (maks 30)" value={LISTING.title} />
        <InfoRow label="Deskripsi singkat (maks 80)" value={LISTING.short} />
        <div className="pt-3">
          <div className="mb-1.5 flex items-center justify-between"><span className="text-xs text-muted-foreground">Deskripsi lengkap</span>
            <Button size="sm" variant="outline" onClick={() => copy(LISTING.full)} className="gap-1.5" data-testid="copy-full-desc"><Copy className="h-3.5 w-3.5" /> Salin</Button></div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-muted p-3 font-sans text-xs leading-relaxed">{LISTING.full}</pre>
        </div>
      </Card>
      <Card icon={ImageIcon} title="Gambar Toko (ketuk untuk unduh)" testid="store-assets">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Asset src="/store/play-store-icon-512.png" label="Ikon Play 512" />
          <Asset src="/store/app-store-icon-1024.png" label="Ikon App Store 1024" />
          <div className="col-span-2"><Asset src="/store/play-feature-graphic-1024x500.png" label="Banner Play 1024×500" /></div>
        </div>
        <div className="mt-5 text-sm font-semibold">Screenshot Android (1080×1920)</div>
        <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-6">{SHOTS.map((s) => <Asset key={s} src={`/store/screenshots-android/${s}.png`} label={`android-${s}`} />)}</div>
        <div className="mt-5 text-sm font-semibold">Screenshot iPhone 6,7" (1290×2796)</div>
        <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-6">{SHOTS.map((s) => <Asset key={s} src={`/store/screenshots-iphone/${s}.png`} label={`iphone-${s}`} />)}</div>
      </Card>
      <Card icon={Smartphone} title="Panduan Android (Play Store)" testid="store-android-guide"><Steps items={ANDROID_STEPS} testid="android-steps" /></Card>
      <Card icon={Apple} title="Panduan iOS (App Store)" testid="store-ios-guide"><Steps items={IOS_STEPS} testid="ios-steps" /></Card>
    </div>
  );
}
